import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { loadStripe } from '@stripe/stripe-js/pure';
import { Elements } from '@stripe/react-stripe-js';

const LOAD_TIMEOUT_MS = 20000;

// Resolve Stripe before mounting a checkout: a rejected module-level promise
// otherwise leaves PaymentElement empty, and cannot be retried by the user.
export default function StripeElements({ options, children }) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ stripe: null, error: false });
  const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  const configured = typeof key === 'string' && /^pk_(test|live)_\S+$/.test(key);

  useEffect(() => {
    if (!configured) return undefined;
    let active = true;
    setState({ stripe: null, error: false });
    const timer = setTimeout(() => {
      if (active) {
        active = false;
        setState({ stripe: null, error: true });
      }
    }, LOAD_TIMEOUT_MS);
    // Also handles synchronous initialization errors; no rejected promise is
    // passed to Elements or left without a rejection handler.
    Promise.resolve().then(() => loadStripe(key)).then(stripe => {
      if (!active) return;
      clearTimeout(timer);
      setState({ stripe, error: !stripe });
    }, () => {
      if (!active) return;
      clearTimeout(timer);
      setState({ stripe: null, error: true });
    });
    return () => { active = false; clearTimeout(timer); };
  }, [key, configured, attempt]);

  if (!configured) return <Alert severity="error" role="alert">
    Card payments are unavailable because payment setup is incomplete. Please contact support.
  </Alert>;

  if (state.error) return <Alert severity="error" role="alert">
    <Typography variant="body2">The secure payment form could not load. Check your connection and allow Stripe in any browser content blocker, then try again. If it still does not load, refresh this page or try another browser.</Typography>
    <Button onClick={() => { setState({ stripe: null, error: false }); setAttempt(value => value + 1); }}>
      Retry loading payment form
    </Button>
  </Alert>;

  if (!state.stripe) return <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
    <CircularProgress size={20} aria-hidden="true" />
    <Typography>Loading secure payment form…</Typography>
  </Box>;

  return <Elements stripe={state.stripe} options={options}>{children}</Elements>;
}
